import { useTranslation } from "react-i18next";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { LabelValue } from "@/Component/Value";
import { Link } from "react-router";
import { Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete02Icon, Edit02Icon } from "@hugeicons/core-free-icons/index";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";

export const useCampaignDetailsTable = (removeServiceCenterColumn = false) => {
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedText } = useLocalizedText();
  const { cachedBasicData } = useCachedBasicData();

  const columns = [
    {
      name: t("ARRIVAL_DAY"),
      key: "day_name_ar",
      render: (value, rowData) => getLocalizedText(value, rowData?.day_name_en),
    },
    {
      name: t("FLIGHT_DATE"),
      key: "flight_date",
      render: (value) => getLocalizedDate(value, DATE_TIME_FORMAT),
    },
    {
      name: t("FLIGHT_NUMBER"),
      key: "flight_number",
    },
    {
      name: t("FLIGHT_AIRLINE"),
      key: "airline_id",
      render: (value) => {
        return (

          <SelectCachedOptions.Display
            optionKey="airlines"
            campareBy="id"
            value={value}
            render={(option) => {
              const airlineName = getLocalizedText(option?.name_ar, option?.name_en) || option?.name;
              return option ? (
                <LabelValue.Value value={`${airlineName} (${option?.code})`} />
              ) : (
                "-"
              );
            }}
          />
        );
      },
    },
    {
      name: t("ENTRY_PORT"),
      key: "entry_port_id",
      render: (value) => {
        return <SelectCachedOptions.Display
          optionKey="ports"
          campareBy="id"
          value={value}
          render={(option) => {
            const portName = getLocalizedText(option?.name_ar, option?.name_en) || option?.name;
            return <LabelValue.Value value={portName || "-"} />
          }}
        />
      },
    },
    {
      name: t("COMING_FROM"),
      key: "coming_from_id",
      render: (value) => {
        const comingFrom = cachedBasicData.directions?.find(
          (direction) => direction.id === value
        );

        return (
          <LabelValue.Value value={!comingFrom ? "-" : comingFrom?.name} />
        );
      },
    },
    {
      name: t("DIRECTION"),
      key: "direction_id",
      render: (value) => {
        return (
          <SelectCachedOptions.Display
            optionKey="directions"
            campareBy="id"
            value={value}
            render={(option) => <LabelValue.Value value={option?.name || "-"} />}
          />
        );
      },
    },
    {
      name: t("BUSES_COUNT"),
      key: "buses_count",
      render: (value) => {
        return <LabelValue.Value value={value || "-"} />
      },
    },
    ...(removeServiceCenterColumn ? [] : [
      {
        name: t("SERVICE_CENTER"),
        key: "service_center_info",
        render: (value) => {
          return <LabelValue.Value value={value?.name || "-"} />;
        },
      },
    ]),
    {
      name: t("PILGRIMS_COUNT"),
      key: "capacity",
    },
    {
    name: t("GROUP_NAME"),
    key: "group_name",
    render: (value, rowData) => {
      return <LabelValue.Value value={value || "-"} />;
    },
    },
    {
      name: t("HOUSING_CONTRACT"),
      key: "contract_id",
      render: (value) => {
        return (
          <LabelValue
            copyValue={value}
            buttonIconSize={12}
            value={value}
            renderValue={(value) =>
              !!value ? (
                <Link to={`/app/makkah/housing-contracts/${value}/view`}>
                  {value}
                </Link>
              ) : (
                "-"
              )
            }
          />
        );
      },
      filterable: false,
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ];

  const getActions =
    ({ edit: editProps, delete: deleteProps }) =>
      (rowData) => {
        return [
          {
            ...(editProps(rowData) || {}),
            key: "edit",
            label: (
              <Stack direction="horizontal" gap={2}>
                <HugeiconsIcon size={20} icon={Edit02Icon} />
                {t("EDIT")}
              </Stack>
            ),
          },
          {
            type: "divider",
          },
          {
            ...(deleteProps(rowData) || {}),
            key: "delete",
            label: (
              <Stack direction="horizontal" gap={2}>
                <HugeiconsIcon size={20} icon={Delete02Icon} />
                {t("DELETE")}
              </Stack>
            ),
            danger: true,
          },
        ];
      };

  return {
    getActions,
    columns,
  };
};
